import React, { useState, useEffect } from 'react';
import { FoxSpinnerInline } from '../ui/FoxSpinner';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Plus, Edit, Trash2, Bell } from 'lucide-react';
import { toast } from 'sonner';

const ResellerNoticesManagement = () => {
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNotice, setEditingNotice] = useState(null);
  const [form, setForm] = useState({ title: '', message: '', button_text: '', button_url: '', active: true });

  useEffect(() => {
    loadNotices();
  }, []);

  const loadNotices = async () => {
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`${BACKEND_URL}/api/reseller-notices/`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotices(response.data);
    } catch (error) {
      console.error('Erro ao carregar avisos:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');

      if (editingNotice) {
        await axios.put(
          `${BACKEND_URL}/api/reseller-notices/${editingNotice.id}`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Aviso atualizado!');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/reseller-notices/`,
          form,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Aviso criado!');
      }

      setDialogOpen(false);
      setForm({ title: '', message: '', button_text: '', button_url: '', active: true });
      setEditingNotice(null);
      loadNotices();
    } catch (error) {
      toast.error('Erro ao salvar aviso');
    }
  };

  const handleEdit = (notice) => {
    setEditingNotice(notice);
    setForm({
      title: notice.title,
      message: notice.message,
      button_text: notice.button_text || '',
      button_url: notice.button_url || '',
      active: notice.active
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remover este aviso?')) return;

    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      await axios.delete(`${BACKEND_URL}/api/reseller-notices/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Aviso removido!');
      loadNotices();
    } catch (error) {
      toast.error('Erro ao remover aviso');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <FoxSpinnerInline size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-xl font-bold">📢 Avisos para Afiliados</h3>
          <p className="text-sm text-gray-600">{notices.length} aviso(s) cadastrado(s)</p>
        </div>
        <Button
          onClick={() => {
            setEditingNotice(null);
            setForm({ title: '', message: '', button_text: '', button_url: '', active: true });
            setDialogOpen(true);
          }}
          className="bg-blue-600"
        >
          <Plus className="mr-2 h-4 w-4" />
          Novo Aviso
        </Button>
      </div>

      {notices.length === 0 ? (
        <p className="text-center text-gray-500 py-8">Nenhum aviso cadastrado</p>
      ) : (
        <div className="space-y-3">
          {notices.map((notice) => (
            <Card key={notice.id} className={notice.active ? 'border-blue-200' : 'border-gray-200'}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <h4 className="font-bold">{notice.title}</h4>
                      <Badge variant={notice.active ? 'default' : 'secondary'}>
                        {notice.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </div>
                    <p className="text-sm text-gray-700 whitespace-pre-wrap">{notice.message}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      📅 {new Date(notice.created_at).toLocaleString('pt-BR')}
                    </p>
                  </div>
                  <div className="flex gap-2 ml-4">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(notice)}
                    >
                      <Edit className="h-3 w-3" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDelete(notice.id)}
                    >
                      <Trash2 className="h-3 w-3" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Dialog Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editingNotice ? 'Editar Aviso' : 'Novo Aviso'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Título</Label>
              <Input
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                placeholder="Ex: Novo sistema de comissões"
                required
                maxLength={200}
              />
            </div>
            <div>
              <Label>Mensagem</Label>
              <Textarea
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Escreva a mensagem do aviso..."
                rows={5}
                required
              />
            </div>
            
            {/* NOVO - Campos do botão (opcionais) */}
            <div className="border-t pt-4">
              <p className="text-sm font-semibold mb-3">🔗 Botão com Link (Opcional)</p>
              <div className="space-y-3">
                <div>
                  <Label>Texto do Botão</Label>
                  <Input
                    value={form.button_text}
                    onChange={(e) => setForm({ ...form, button_text: e.target.value })}
                    placeholder="Ex: Entre no Grupo do WhatsApp"
                    maxLength={100}
                  />
                </div>
                <div>
                  <Label>Link (URL)</Label>
                  <Input
                    value={form.button_url}
                    onChange={(e) => setForm({ ...form, button_url: e.target.value })}
                    placeholder="Ex: https://chat.whatsapp.com/..."
                    maxLength={500}
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Se preencher, botão aparecerá no aviso para afiliados
                  </p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              <Switch
                checked={form.active}
                onCheckedChange={(checked) => setForm({ ...form, active: checked })}
              />
              <Label>Aviso Ativo</Label>
            </div>
            <div className="flex gap-2">
              <Button type="submit" className="flex-1">
                Salvar
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
                className="flex-1"
              >
                Cancelar
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ResellerNoticesManagement;
